<script lang="ts" setup>
import type { FormModel as ModuleModel } from '#layers/studio/app/runtime/services/ModulesService';
import ModulesService from '#layers/studio/app/runtime/services/ModulesService';
import DocumentsService from '#layers/studio/app/runtime/services/DocumentsService';
import { FluentFormBuilder } from '#layers/studio/app/components/forms';
import type { FormBuilderContract } from '#layers/studio/app/runtime/builder/form';
import type { FormEntry } from '#layers/studio/app/components/forms/blueprints/form';
import { Definition as TextDefinition } from '#layers/studio/app/components/forms/blueprints/text';
import type { HasLabel } from '#layers/studio/app/runtime/builder/form/traits/HasLabel';
import FormsService from '#layers/studio/app/runtime/services/FormsService';

defineOptions({
    name: 'WidgetDynamicSettings'
});

const { $alert } = useAlerts();
const { $log } = useLogging();
const { $t } = useLocalization();

interface FormSettings
{
    settings: {
        symbol: string;
        moduleId: number;
        sectionId: string;
        sourceType: 'Aql' | 'List';
        documentsIds: number[];
        query: number;
        fieldName: string;
    };
}

const documentId = ref<number>();
const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    symbol: null,
    moduleId: null,
    sectionId: null,
    sourceType: 'Aql',
    documentsIds: [],
    query: null,
    fieldName: ''
};
const modules = ref<Option<number>[]>([]);
const module = ref<ModuleModel>();
const sections = computed(() => ([
    { value: null, text: '' },
    ...(module.value?.sections.map(p => ({ value: p.id, text: p.name })) ?? [])
]));
const section = computed(() => module.value?.sections.find(p => p.id == form.value.settings.sectionId));

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };

    await loadModules();
    await loadModule(form.value.settings.moduleId);
});

watch(() => form.value?.settings, () =>
{
    model.value = form.value;
},
{ deep: true });

watch(() => form.value?.settings.moduleId, async (value, old) =>
{
    if (value != old)
    {
        form.value.settings.sectionId = null;
    }

    await loadModule(value);
});

async function loadModules()
{
    try
    {
        const pager = new Pager(1, 10000, 'Name', 'ASC');
        const response = await ModulesService.getList({} as any, pager);

        modules.value = [{ value: null, text: '' }, ...response.items.map(p => ({
            value: p.id,
            text: p.name
        }))];
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return false;
    }

    return true;
}

async function loadModule(moduleId: number)
{
    if (!moduleId) return;

    try
    {
        const response = await ModulesService.fetch(moduleId);

        module.value = response.result;
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
}

const document = defineModel<FormEntry>('document');

const fields = computedAsync(async () =>
{
    const result = await FormsService.fetchPublished(section.value.formId);

    const builder = ref(FluentFormBuilder
        .setup(false, true)
        .blueprint(() => result.definition, () => section.value.formId)
        .entry(() => document.value, () => 0)
        .make()) as Ref<FormBuilderContract>;

    const a = builder.value.schema
        .components(TextDefinition.type)
        .map(p => ({ value: p.name, text: builder.value.localization.translate((p as HasLabel).label) }));

    return a;
});

async function searchDocument(query: string, limit: number)
{
    return await DocumentsService.getAutocompleteList(section.value.formId, form.value.settings.fieldName, query, limit, form.value.settings.documentsIds);
}

const documents = computedAsync(async () =>
{
    try
    {
        return await DocumentsService.getListByIds(section.value.formId, form.value.settings.fieldName, form.value.settings.documentsIds);
    }
    catch (ex)
    {
        return [];
    }
});

async function addDocument()
{
    if (documentId.value == null)
        return;

    form.value.settings.documentsIds.push(documentId.value);

    documentId.value = null;
}

async function removeDocument(id: number)
{
    form.value.settings.documentsIds = form.value.settings.documentsIds.filter(p => p !== id);
}

async function validate()
{
    form.value.validate(schema => ({
        settings: schema.object({
            symbol: schema.string().nullable().required($t('[[[Podanie nazwy symbolu jest wymagane]]]')),
            moduleId: schema.number().nullable().required($t('[[[Wybraniue modułu jest wymagane]]]')),
            sectionId: schema.string().nullable().required($t('[[[Wybraniue sekcji jest wymagane]]]'))
        })
    }));
}

defineExpose({
    validate
});
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Symbol]]]')" :invalid-feedback="form.$errors.first('settings.symbol')" :state="form.$errors.state('settings.symbol')" required>
        <IdeoFormInput v-model="form.settings.symbol" type="text" name="settings.symbol" :maxlength="20" />
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Moduł dynamiczny]]]')" :invalid-feedback="form.$errors.first('settings.moduleId')" :state="form.$errors.state('settings.moduleId')" required>
        <IdeoFormSelect v-model="form.settings.moduleId" type="text" name="settings.moduleId" :options="modules" />
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Sekcja modułu]]]')" :invalid-feedback="form.$errors.first('settings.sectionId')" :state="form.$errors.state('settings.sectionId')" required>
        <IdeoFormSelect v-model="form.settings.sectionId" type="text" name="settings.sectionId" :options="sections" :disabled="!form.settings.moduleId" />
    </IdeoFormGroup>
    <div v-if="section && section.template == 'Settings'" class="alert alert-info">
        {{ $t('Ta sekcja modułu jest skonfigurowana jako pojedynczy formularz. Wszystkie pola pola formularza zostaną zwrócone jako jeden obiekt.') }}
    </div>
    <div v-else-if="section">
        <IdeoFormRadio v-model="form.settings.sourceType" :value="'Aql'" name="sourceType">
            {{ $t('[[[Zapytanie AQL]]]') }}
        </IdeoFormRadio>
        <IdeoFormRadio v-model="form.settings.sourceType" :value="'List'" name="sourceType">
            {{ $t('[[[Wybierz z listy]]]') }}
        </IdeoFormRadio>
        <IdeoFormGroup v-if="form.settings.sourceType == 'Aql'" :label="$t('[[[AQL]]]')" :invalid-feedback="form.$errors.first('settings.query')" :state="form.$errors.state('settings.query')">
            <IdeoFormTextarea v-model="form.settings.query" type="text" name="settings.query" :rows="10" />
        </IdeoFormGroup>
        <div v-if="form.settings.sourceType == 'List'">
            <IdeoFormGroup :label="$t('[[[Pole identyfikujące dokument]]]')" :invalid-feedback="form.$errors.first('settings.fieldName')" :state="form.$errors.state('settings.fieldName')">
                <IdeoFormSelect v-model="form.settings.fieldName" type="text" name="settings.fieldName" :options="fields" />
            </IdeoFormGroup>
            <IdeoFormGroup v-if="form.settings.fieldName" :label="$t('[[[Wybierz dokument]]]')">
                <div class="d-flex flex-column flex-md-row">
                    <div class="flex-grow-1">
                        <IdeoAutocomplete
                            v-model="documentId"
                            name="documentId"
                            track-by="id"
                            :placeholder="$t('[[[zacznij pisać aby wyszukać dokument...]]]')"
                            :fetch="() => null"
                            :search="(query: string, limit: number) => searchDocument(query, limit)"
                            :refresh="true"
                        >
                            <template #selected="{ option }">
                                <div class="text-wrap">
                                    {{ option.result[form.settings.fieldName] }}
                                </div>
                            </template>
                            <template #option="{ option }">
                                <div class="text-wrap">
                                    {{ option.result[form.settings.fieldName] }}
                                </div>
                            </template>
                        </IdeoAutocomplete>
                    </div>
                    <div class="d-flex align-items-stretch">
                        <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addDocument()">
                            <i class="fas fa-plus" />
                        </button>
                    </div>
                </div>
            </IdeoFormGroup>
            <IdeoFormGroup v-if="form.settings.fieldName" :label="$t('[[[Dokumenty]]]')">
                <div v-if="documents" class="list-group">
                    <div v-for="item in documents" :key="item.id" class="list-group-item p-2 d-flex gap-1 list-group-item-action">
                        <div class="d-flex align-items-center me-auto">
                            <span class="me-2">
                                <i class="fad fa-fw fa-browser text-primary" />
                            </span>
                            <span class="me-auto">
                                <span class="d-block" :title="$t('[[[Nazwa]]]')">
                                    <b>{{ item.result[form.settings.fieldName] }}</b>
                                </span>
                            </span>
                        </div>

                        <span class="d-flex align-items-center">
                            <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-related-${item.id}`" :value="item.id" @confirm="removeDocument(item.id)" />
                            <IdeoButton :id="`btn-delete-related-${item.id}`" variant="danger" size="sm" pill :title="$t('[[[Usuń dokument]]]')">
                                <i class="fas fa-trash-alt" />
                            </IdeoButton>
                        </span>
                    </div>
                </div>
            </IdeoFormGroup>
        </div>

        <div class="alert alert-info">
            {{ $t('Ta sekcja modułu jest skonfigurowana jako kolekcja wpisów. Wszystkie wpisy zostaną zwrócone jako tablica obiekt. Zwracane dane mogą być dodatkowo skonfigurowane za pomocą zapytania AQL.') }}
        </div>
    </div>
</template>
